/**
 * PastSimulationsTable - Display past outcome simulations for a record
 *
 * Atomic level: Organism
 * Composes: Table, Badge, Button
 *
 * Groups simulations by date and displays outcome, signal summary, and status.
 * Integrates into RecordDetail Investigation section.
 */

import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Timestamp } from '../../../ui/timestamp';
import { cn } from '../../../../lib/utils';

// =============================================================================
// Types
// =============================================================================

export interface Simulation {
  id: string;
  createdAt: string;
  outcome: 'upheld' | 'not_upheld' | 'partially_upheld' | 'unknown';
  confidence: number; // 0-100
  signalSummary: string;
  signalDetail: string;
  status: 'completed' | 'pending' | 'failed' | 'draft';
}

interface PastSimulationsTableProps {
  simulations: Simulation[];
  onViewSimulation?: (simulationId: string) => void;
  className?: string;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Group simulations by date
 */
const groupByDate = (simulations: Simulation[]): Record<string, Simulation[]> => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const grouped: Record<string, Simulation[]> = {};

  simulations.forEach((sim) => {
    const simDate = new Date(sim.createdAt);
    simDate.setHours(0, 0, 0, 0);

    let label: string;
    if (simDate.getTime() === today.getTime()) {
      label = 'Today';
    } else if (simDate.getTime() === yesterday.getTime()) {
      label = 'Yesterday';
    } else {
      // Format as "Mon 30 Sep 2025"
      label = simDate.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }

    if (!grouped[label]) {
      grouped[label] = [];
    }
    grouped[label].push(sim);
  });

  return grouped;
};

/**
 * Format outcome label
 */
const outcomeLabel = (outcome: Simulation['outcome']): string => {
  switch (outcome) {
    case 'upheld':
      return 'Likely Upheld';
    case 'not_upheld':
      return 'Likely Not Upheld';
    case 'partially_upheld':
      return 'Likely Partially Upheld';
    case 'unknown':
    default:
      return 'Unknown';
  }
};

/**
 * Get outcome badge variant based on outcome
 */
const outcomeBadgeVariant = (
  outcome: Simulation['outcome']
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (outcome) {
    case 'upheld':
      return 'destructive'; // Red = bad for firm
    case 'not_upheld':
      return 'default'; // Green = good for firm
    case 'partially_upheld':
      return 'secondary'; // Yellow = mixed
    case 'unknown':
    default:
      return 'outline'; // Gray = uncertain
  }
};

/**
 * Get status badge variant
 */
const statusBadgeVariant = (
  status: Simulation['status']
): 'default' | 'secondary' | 'outline' | 'destructive' => {
  switch (status) {
    case 'completed':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'failed':
      return 'destructive';
    case 'draft':
    default:
      return 'outline';
  }
};

// =============================================================================
// Component
// =============================================================================

export const PastSimulationsTable: React.FC<PastSimulationsTableProps> = ({
  simulations,
  onViewSimulation,
  className,
}) => {
  // Sort simulations by date (most recent first)
  const sortedSimulations = useMemo(() => {
    return [...simulations].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [simulations]);

  // Group by date
  const grouped = useMemo(
    () => groupByDate(sortedSimulations),
    [sortedSimulations]
  );

  if (simulations.length === 0) {
    return (
      <div className={cn('text-center py-8', className)}>
        <p className="text-sm text-muted-foreground">
          No past cases yet. Run your first case guidance to get started.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {Object.entries(grouped).map(([dateLabel, sims]) => (
        <div key={dateLabel}>
          {/* Date header */}
          <div className="flex items-center gap-2 mb-3">
            <h4 className="text-sm font-medium text-foreground">{dateLabel}</h4>
            <Badge variant="secondary" className="text-xs">
              {sims.length} {sims.length === 1 ? 'case' : 'cases'}
            </Badge>
          </div>

          {/* Simulations table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Time</TableHead>
                <TableHead className="w-[140px]">Outcome</TableHead>
                <TableHead>Signal</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sims.map((sim) => (
                <TableRow key={sim.id} className="group">
                  {/* Time */}
                  <TableCell className="text-sm text-muted-foreground">
                    <Timestamp value={sim.createdAt} format="time-only" />
                  </TableCell>

                  {/* Outcome */}
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge
                        variant={outcomeBadgeVariant(sim.outcome)}
                        className="w-fit text-xs"
                      >
                        {outcomeLabel(sim.outcome)}
                      </Badge>
                      {sim.confidence > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {sim.confidence}% confidence
                        </span>
                      )}
                    </div>
                  </TableCell>

                  {/* Signal summary and detail */}
                  <TableCell>
                    <div className="flex flex-col gap-1 max-w-md">
                      <p className="text-sm font-medium truncate">
                        {sim.signalSummary}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {sim.signalDetail}
                      </p>
                    </div>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Badge
                      variant={statusBadgeVariant(sim.status)}
                      className="text-xs capitalize"
                    >
                      {sim.status}
                    </Badge>
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="text-right">
                    {onViewSimulation && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewSimulation(sim.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        View Details
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
};
